/**
 * ProvenanceFilter
 *
 * The segmented control above any Org page that mixes owned data with data rented via Creator
 * assignments (UPDATE.md §8.3, §8.4):
 *
 *   [ All · Owned · Rented · Promoted ]
 *
 * Pure presentational — the parent owns the active value and decides what to do (typically:
 * filter a list of rows where row.provenance.kind matches). Counts are optional and shown after
 * each label, so the user knows how much data is behind each segment.
 */

import React from 'react';
import { string, number, func, shape, oneOf } from 'prop-types';
import { useIntl } from '../../util/reactIntl';
import SegmentedControl from '../SegmentedControl/SegmentedControl';

// 'promoted' is a permanently distinct third state: contacts a member chose
// to share with the Org through the Dual Opt-In wizard. They are never merged
// into 'owned' — keeping them visible is what makes the non-extractive claim
// checkable rather than aspirational.
export const PROVENANCE_VALUES = ['all', 'owned', 'rented', 'promoted'];

const ProvenanceFilter = props => {
  const { className, value, onChange, counts } = props;
  const intl = useIntl();
  const options = PROVENANCE_VALUES.map(key => ({
    key,
    label: intl.formatMessage({ id: `ProvenanceFilter.${key}` }),
    count: counts?.[key],
  }));
  return (
    <SegmentedControl className={className} options={options} value={value} onChange={onChange} />
  );
};

ProvenanceFilter.defaultProps = {
  className: null,
  counts: null,
};

ProvenanceFilter.propTypes = {
  className: string,
  value: oneOf(PROVENANCE_VALUES).isRequired,
  onChange: func.isRequired,
  counts: shape({ all: number, owned: number, rented: number, promoted: number }),
};

export default ProvenanceFilter;
